import { PURCHASER_API_BASE_URL, getHeaders } from "../Constants";

export type LevelingViewOutputFormat = "Text" | "Table" | "Spreadsheet";

export type LevelingViewOutputConfig = {
    outputFormat: LevelingViewOutputFormat;
    promptText: string;
    title: string;
    hideNoBids: boolean;
    includeCaveats: boolean;
    columns?: { name: string; description: string }[];
    allowAdditive?: boolean | null;
    includePostamble?: boolean | null;
};

export type RunLevelingViewBody = {
    levelingViewId?: string;
    adHocConfig?: LevelingViewOutputConfig;
};

export type LevelingViewOutputSummary = {
    id: string;
    rfqId: number;
    levelingViewId: string | null;
    title: string;
    prompt: string;
    weight?: number;
    pinned: boolean;
    outputFormat: LevelingViewOutputFormat;
    markdown?: string | null;
    tableId?: number | null;
    createdAt: string;
    updatedAt: string;
    deletedAt?: string | null;
    levelingView?: {
        id: string;
        title: string;
        promptText: string;
        outputFormat: LevelingViewOutputFormat;
        hideNoBids: boolean;
        includeCaveats: boolean;
        allowAdditive?: boolean | null;
        includePostamble?: boolean | null;
        columns?: { name: string; description: string }[] | null;
        alwaysRun?: boolean;
    } | null;
};

export type CitationSourceDetail = {
    factId: number;
    mailboxId: number;
    vendorId: number | null;
    vendorName: string | null;
    fileUploadId: number;
    fileUpload: {
        id: number;
        originalFilename: string | null;
        internalFilename: string | null;
        mimeType: string | null;
    } | null;
    pageNumber: number | null;
    sourceLocators: unknown;
    summary: string;
    rawContext: string;
    factType: string;
    confidence: number;
};

export type TabularDataStoreColumn = {
    id: string;
    name: string;
    dataType?: string;
    alignment?: "left" | "center" | "right";
};

export type TabularDataStoreRow = {
    id: number;
    cellValues: Record<string, { value: any; dataType?: string }>;
};

export type TabularDataStoreDetail = {
    id: number;
    name: string;
    columnDefinitions: TabularDataStoreColumn[];
    rows: TabularDataStoreRow[];
};

// Stored leveling view config (team-scoped template)
export type LevelingView = {
    id: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    teamId: number;
    createdByUserId: number;
    title: string;
    promptText: string;
    alwaysRun: boolean;
    outputFormat: LevelingViewOutputFormat;
    hideNoBids: boolean;
    includeCaveats: boolean;
    allowAdditive: boolean | null;
    includePostamble: boolean | null;
    columns: { name: string; description: string }[] | null;
    /** True for the view a team's purchase orders extract against, which isn't an RFQ template. */
    isPoLineItemsView: boolean;
};

// Request body for creating or updating a stored leveling view config
export type LevelingViewBody = {
    outputFormat: LevelingViewOutputFormat;
    promptText: string;
    title: string;
    hideNoBids: boolean;
    includeCaveats: boolean;
    columns?: { name: string; description: string }[];
    allowAdditive?: boolean | null;
    includePostamble?: boolean | null;
    alwaysRun?: boolean;
};

export type LevelingViewOutputDetail = {
    id: string;
    rfqId: number;
    levelingViewId: string | null;
    outputFormat: LevelingViewOutputFormat;
    title: string;
    prompt: string;
    createdAt: string;
    updatedAt: string;
    pinned: boolean;
    markdown: string | null;
    tableId: number | null;
    tabularDataStore: TabularDataStoreDetail | null;
    citationsMap: Record<number | string, CitationSourceDetail>;
    levelingView?: LevelingViewOutputSummary["levelingView"];
    spreadsheet?: {
        id: string;
        version: number;
        createdAt: string;
        contentJson: Record<string, any>;
        createdByUserId?: number | null;
        spreadsheetId?: string;
    } | null;
    contentJson?: Record<string, any> | null;
};

export default {
    triggerLevelingViewRun: async (
        rfqPublicId: string,
        body: RunLevelingViewBody,
    ): Promise<{
        status: string;
        asyncRunId: number;
        rfqId: number;
        levelingViewId?: string;
        invocationId?: string;
    }> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/rfqs/${rfqPublicId}/leveling-views`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(body),
        });
        if (!res.ok) {
            throw new Error(`Failed to trigger leveling view run: ${res.statusText}`);
        }
        return res.json();
    },

    listLevelingViewOutputs: async (
        rfqPublicId: string,
    ): Promise<{ outputs: LevelingViewOutputSummary[] }> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/rfqs/${rfqPublicId}/leveling-views/outputs`, {
            headers: getHeaders(),
        });
        if (!res.ok) {
            throw new Error(`Failed to fetch leveling view outputs: ${res.statusText}`);
        }
        return res.json();
    },

    getLevelingViewOutput: async (
        rfqPublicId: string,
        outputId: string,
    ): Promise<LevelingViewOutputDetail> => {
        const res = await fetch(
            `${PURCHASER_API_BASE_URL}/rfqs/${rfqPublicId}/leveling-views/outputs/${outputId}`,
            {
                headers: getHeaders(),
            },
        );
        if (!res.ok) {
            throw new Error(`Failed to fetch leveling view output: ${res.statusText}`);
        }
        return res.json();
    },

    // --- Stored leveling view config CRUD (team-scoped, not RFQ-scoped) ---

    // List all stored leveling view configs for the team
    listLevelingViews: async (): Promise<LevelingView[]> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/leveling-views`, {
            headers: getHeaders(),
        });
        if (!res.ok) throw new Error(`Failed to list leveling views: ${res.statusText}`);
        return res.json();
    },

    // Create a new stored leveling view config
    createLevelingView: async (body: LevelingViewBody): Promise<LevelingView> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/leveling-views`, {
            method: "POST",
            headers: getHeaders(),
            body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`Failed to create leveling view: ${res.statusText}`);
        return res.json();
    },

    // Update an existing stored leveling view config
    updateLevelingView: async (levelingViewId: string, body: LevelingViewBody): Promise<LevelingView> => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/leveling-views/${levelingViewId}`, {
            method: "PUT",
            headers: getHeaders(),
            body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`Failed to update leveling view: ${res.statusText}`);
        return res.json();
    },

    // Soft-delete a stored leveling view config
    deleteLevelingView: async (levelingViewId: string): Promise<LevelingView> => {
        // Omit Content-Type — Fastify rejects empty JSON bodies on DELETE
        const { "Content-Type": _, ...headers } = getHeaders();
        const res = await fetch(`${PURCHASER_API_BASE_URL}/leveling-views/${levelingViewId}`, {
            method: "DELETE",
            headers,
        });
        if (!res.ok) throw new Error(`Failed to delete leveling view: ${res.statusText}`);
        return res.json();
    },

    // --- RFQ-scoped output operations ---

    // Reorders leveling view outputs for an RFQ
    reorderLevelingViewOutputs: async (
        rfqPublicId: string,
        outputIds: string[],
    ): Promise<{ success: boolean }> => {
        const res = await fetch(
            `${PURCHASER_API_BASE_URL}/rfqs/${rfqPublicId}/leveling-views/outputs/reorder`,
            {
                method: "PUT",
                headers: getHeaders(),
                body: JSON.stringify({ outputIds }),
            },
        );
        if (!res.ok) {
            throw new Error(`Failed to reorder leveling view outputs: ${res.statusText}`);
        }
        return res.json();
    },

    // Toggles the pinned state of a leveling view output
    togglePinLevelingViewOutput: async (
        rfqPublicId: string,
        outputId: string,
        pinned: boolean,
    ): Promise<{ success: boolean; pinned: boolean }> => {
        const res = await fetch(
            `${PURCHASER_API_BASE_URL}/rfqs/${rfqPublicId}/leveling-views/outputs/${outputId}/pin`,
            {
                method: "PATCH",
                headers: getHeaders(),
                body: JSON.stringify({ pinned }),
            },
        );
        if (!res.ok) {
            throw new Error(`Failed to toggle pin state: ${res.statusText}`);
        }
        return res.json();
    },

    // Soft-deletes an output (sets deletedAt so it's excluded from list responses)
    deleteLevelingViewOutput: async (
        rfqPublicId: string,
        outputId: string,
    ): Promise<void> => {
        // Omit Content-Type — Fastify rejects empty JSON bodies on DELETE
        const { "Content-Type": _, ...headers } = getHeaders();
        const res = await fetch(
            `${PURCHASER_API_BASE_URL}/rfqs/${rfqPublicId}/leveling-views/outputs/${outputId}`,
            {
                method: "DELETE",
                headers,
            },
        );
        if (!res.ok) {
            throw new Error(`Failed to delete leveling view output: ${res.statusText}`);
        }
    },
};
